import { createPragmaticTheme } from 'pragmatic-ui';
// import { darken, lighten } from '@mui/material/styles';
import { lighten } from '@mui/material/styles';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Get initial mode from localStorage or default to 'dark'
const getInitialMode = (): 'light' | 'dark' => {
  try {
    const storedMode = localStorage.getItem('colorMode');
    return storedMode === 'light' || storedMode === 'dark'
      ? storedMode
      : 'dark';
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return 'dark';
  }
};

// Initialize with the stored/default mode
const initialMode = getInitialMode();

type ColorModeContextArgs = {
  toggleColorMode: () => void;
  mode: 'light' | 'dark';
};

export const ColorModeContext = createContext<ColorModeContextArgs>({
  toggleColorMode: () => {},
  mode: initialMode,
});

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider = ({ children }: { children: any }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(initialMode);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateModeBasedOnURL = () => {
      const isAdminRoute = window.location.pathname.includes('/admin');

      if (isAdminRoute && mode !== 'dark') {
        setMode('dark');
      }
    };

    updateModeBasedOnURL();

    const handleRouteChange = () => {
      updateModeBasedOnURL();
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem('colorMode', newMode);
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
      return newMode;
    });
  };

  const contextValue = useMemo(
    () => ({
      mode,
      toggleColorMode,
    }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      {children}
    </ColorModeContext.Provider>
  );
};

const getPragmaticColorThemeByMode = (mode: 'dark' | 'light') => {
  return createPragmaticTheme({
    borderRadius: 0,
    options: {
      palette: {
        mode,
        primary: {
          main: '#F6AE2D',
          contrastText: '#202D38',
        },
        secondary: {
          main: '#FFFFFF29',
          contrastText: mode === 'dark' ? '#fff' : '#202D38',
        },
        background: {
          default: mode === 'dark' ? '#121212 !important' : '#fff',
          paper: mode === 'dark' ? '#202D38' : '#EEE !important',
        },
        text: {
          primary: mode === 'dark' ? '#FFF' : '#202D38',
          secondary: '#757575',
        },
      },
      typography: {
        fontFamily: '"Figtree", sans-serif',
        fontWeightLight: 400,
        fontWeightRegular: 500,
        fontWeightMedium: 600,
        fontWeightBold: 800,
        body1: {
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '22px' /* 137.5% */,
          letterSpacing: '0.25px',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        body2: {
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '22px' /* 137.5% */,
          letterSpacing: '0.15px',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        subtitle1: {
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 800,
          lineHeight: '24px' /* 137.5% */,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        subtitle2: {
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 800,
          lineHeight: 'normal',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        caption: {
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: 'normal',
          letterSpacing: '0.15px',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        h1: {
          fontSize: '96px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '112.032px' /* 116.7% */,
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        h2: {
          fontSize: '64px',
          fontStyle: 'normal',
          fontWeight: 800,
          lineHeight: '68px',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        h3: {
          fontSize: '56px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        h4: {
          fontSize: '34px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal' /* 106.25% */,
          letterSpacing: '0.25px',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        h5: {
          fontSize: '26px',
          fontStyle: 'normal',
          fontWeight: 800,
          lineHeight: 'normal',
          letterSpacing: '0.5px',
          fontFeatureSettings: "'clig' off, 'liga' off",
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
        h6: {
          fontSize: '20px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '30px',
          letterSpacing: '0.15px',
          color: mode === 'dark' ? '#FFF' : '#202D38',
        },
      },
      components: {
        MuiButton: {
          variants: [
            {
              props: { variant: 'contained', color: 'secondary' },
              style: {
                borderRadius: '26px',
                '&:hover': {
                  backgroundColor: '#FFFFFF29',
                },
              },
            },
            {
              props: { variant: 'contained', color: 'primary' },
              style: {
                borderRadius: '26px',
                '&:hover': {
                  backgroundColor: lighten('#F6AE2D', 0.09),
                },
              },
            },
          ],
          styleOverrides: {
            root: {
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '18px',
              letterSpacing: '0.4px',
              textTransform: 'capitalize',
              paddingLeft: '20px',
              paddingRight: '20px',
              boxShadow: 'none',
              borderRadius: '26px',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: mode === 'dark' ? '#202D38' : '#EEE',
              backgroundImage: 'none !important',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: '#202D38',
              borderRight: 'none !important',
            },
            root: {
              backgroundColor: 'transparent',
              '&.MuiDrawer-docked': {
                backgroundColor: 'transparent',
              },
            },
            docked: {
              backgroundColor: 'transparent',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#202D38',
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              fontWeight: '600',
              fontSize: '14px',
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              padding: '16px',
              borderRadius: '12px !important',
              '&, & .MuiListItemIcon-root, & .MuiTypography-root': {
                lineHeight: 'normal !important',
                letterSpacing: '0.15px !important',
              },
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
              },
              '&:first-of-type.Mui-selected, &:first-of-type.Mui-selected:hover':
                {
                  backgroundColor: 'rgba(255, 255, 255, 0.16)',
                },
              // '&:not(:first-of-type)': {
              //   '& .Mui-selected': {
              //     backgroundColor: 'rgba(255, 255, 255, 0.16)',
              //   },
              //   '&.Mui-selected, &.Mui-selected:hover': {
              //     backgroundColor: 'rgba(255, 255, 255, 0.16) !important',
              //   },
              // },
            },
          },
        },
        MuiInput: {
          styleOverrides: {
            underline: {
              '&:before': {
                borderBottomColor: mode === 'dark' ? '#fff' : '#202D38',
              },
              '&:hover:not(.Mui-disabled):before': {
                borderBottomColor: '#fff',
              },
              '&:after': {
                borderBottomColor: '#F6AE2D',
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              color: mode === 'dark' ? '#fff !important' : '#202D38 !important',
              background: mode === 'dark' ? '#202D38' : '#EEE',
              '&:hover': {
                background:
                  mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08) !important'
                    : 'rgba(25, 25, 25, 0.06) !important',
              },
              '&.Mui-selected': {
                backgroundColor:
                  mode === 'dark'
                    ? 'transparent !important'
                    : 'rgba(25, 25, 25, 0.06) !important',
                color: '#F6AE2D !important', // Optional: change text color for selected state if needed
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              '&.MuiInputBase-root': {
                '&.MuiFilledInput-root': {
                  background:
                    mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : '#FFE6B0',
                  border: 'none',
                  borderRadius: '4px',
                },
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              color: '#F6AE2D',
              '&:focus': {
                outline: 'none',
                border: 'none',
              },
            },
          },
        },
        MuiButtonBase: {
          styleOverrides: {
            root: {
              '&:focus': {
                outline: 'none',
                border: 'none',
              },
            },
          },
        },
      },
    },
  });
};

export default getPragmaticColorThemeByMode;
